import {
  Challenge,
  ChallengeCard,
  Event,
  Geogroup,
  GeogroupService,
  HighlightedEventCard,
  TChallengePeriod,
  UserChallenge,
  UserGeogroup,
  UserService,
  useCancellablePromise,
} from '@geovelo-frontends/commons';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Link, navigate } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../components/button';
import { AppContext } from '../../context';

function Challenges({
  geogroup,
  userGeogroup,
  highlightedEvent,
  highlightedEventChallenge,
  setHighlightedEventChallenge,
}: {
  geogroup: Geogroup | undefined;
  highlightedEvent: Event | null | undefined;
  highlightedEventChallenge: Challenge | null | undefined;
  setHighlightedEventChallenge: (challenge: Challenge | null) => void;
  userGeogroup?: UserGeogroup | null;
}): React.JSX.Element {
  const [page] = useState(1);
  const [challenges, setChallenges] = useState<Challenge[]>();
  const [userChallenges, setUserChallenges] = useState<UserChallenge[]>();
  const [joiningChallenge, setJoiningChallenge] = useState<{ [key: number]: boolean }>({});
  const [selectedPeriod, selectPeriod] = useState<TChallengePeriod>('current');
  const {
    user: { current: currentUser },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();
  const {
    cancellablePromise: cancellableUserChallengesPromise,
    cancelPromises: cancelUserChallengesPromises,
  } = useCancellablePromise();

  useEffect(() => {
    if (geogroup) getChallenges(geogroup.id);

    return () => {
      cancelPromises();
      setChallenges(undefined);
    };
  }, [geogroup, page, selectedPeriod]);

  useEffect(() => {
    if (geogroup && currentUser && challenges) {
      if (challenges.length > 0) getUserChallenges(geogroup.id, challenges);
      else setUserChallenges([]);
    } else if (currentUser === null) {
      setUserChallenges([]);
    }

    return () => cancelUserChallengesPromises();
  }, [geogroup, currentUser, challenges]);

  async function getChallenges(geogroupId: number) {
    try {
      const { challenges } = await cancellablePromise(
        GeogroupService.getChallenges(geogroupId, {
          page,
          pageSize: 100,
          order: '-start_datetime',
          state:
            selectedPeriod === 'future'
              ? ['future']
              : selectedPeriod === 'past'
                ? ['finished']
                : ['running'],
        }),
      );

      setChallenges(challenges);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.challenges.server_error'), { variant: 'error' });
      }
    }
  }

  async function getUserChallenges(geogroupId: number, challenges: Challenge[]) {
    try {
      const userChallenges = await cancellableUserChallengesPromise(
        UserService.getUserChallenges({
          groupIds: [geogroupId],
          page: 1,
          pageSize: 100,
          query:
            '{leaderboard, progress_value, day_streak_details, challenge{id, publication_status, target_type, trace_type}}',
          challengeIds: challenges.map(({ id }) => id),
        }),
      );

      setUserChallenges(userChallenges);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.challenges.server_error'), {
          variant: 'error',
        });
        setChallenges(undefined);
      }
    }
  }

  async function handleChallengeJoin(challenge: Challenge) {
    if (!geogroup) return;

    setJoiningChallenge({ ...joiningChallenge, [challenge.id]: true });

    try {
      await cancellablePromise(
        UserService.joinChallenge({ geogroupId: geogroup.id, challengeId: challenge.id }),
      );

      navigate(`/${currentLanguage}/communities/${geogroup.id}/challenges/${challenge.id}`);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.challenges.server_error'), { variant: 'error' });
      }
    }

    setJoiningChallenge({ ...joiningChallenge, [challenge.id]: true });
  }

  return (
    <>
      <Box display="flex" flexDirection="column" gap={4}>
        <Typography component="h2" fontWeight={700} variant="h6">
          {t('geovelo.communities.challenges.title')}
        </Typography>
        {geogroup &&
          userGeogroup &&
          geogroup.type !== 'other' &&
          geogroup.type !== 'company' &&
          !geogroup.partner &&
          userGeogroup.isAdmin && (
            <HighlightedEventCard
              Button={Button}
              displayButtonProps={
                highlightedEventChallenge
                  ? ({
                      component: Link,
                      to: `/${currentLanguage}/communities/${geogroup.id}/challenges/${highlightedEventChallenge.id}`,
                      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                    } as any)
                  : {}
              }
              geogroup={geogroup}
              highlightedEvent={highlightedEvent}
              highlightedEventChallenge={highlightedEventChallenge}
              onJoined={(challenge) => {
                setHighlightedEventChallenge(challenge);
                navigate(
                  `/${currentLanguage}/communities/${geogroup.id}/challenges/${challenge.id}`,
                );
              }}
            />
          )}
        <Box display="flex" gap={5}>
          {['current', 'future', 'past'].map((period) => (
            <ButtonBase
              key={period}
              onClick={() => selectPeriod(period as TChallengePeriod)}
              sx={{ borderBottom: period === selectedPeriod ? '2px solid #326AC2' : undefined }}
            >
              <Typography
                color={period === selectedPeriod ? '#326AC2' : '#666666'}
                fontWeight={600}
              >
                <Trans i18nKey={`commons.periods.${period}`} />
              </Typography>
            </ButtonBase>
          ))}
        </Box>
        <Box display="flex" flexWrap="wrap" gap={3}>
          {!geogroup || !challenges || !userChallenges ? (
            [0, 1, 3].map((key) => (
              <Box flexShrink={0} key={key} width={{ xs: '100%', md: 'calc((100% - 24px) / 2)' }}>
                <ChallengeCard disableLink enableUserProgression />
              </Box>
            ))
          ) : challenges.length === 0 ? (
            <Typography>{t('geovelo.communities.no_challenge_for_period')}</Typography>
          ) : (
            challenges?.map((challenge) => {
              const userChallenge =
                userChallenges.find(
                  ({ challenge: { id: challengeId } }) => challengeId === challenge.id,
                ) || null;
              const joinable = challenge.period !== 'past' && !userChallenge;

              return (
                <Box
                  flexShrink={0}
                  key={challenge.id}
                  width={{ xs: '100%', md: 'calc((100% - 24px) / 2)' }}
                >
                  <ChallengeCard
                    enableUserProgression
                    challenge={challenge}
                    disableLink={joiningChallenge[challenge.id] || !userChallenge}
                    handleClick={joinable ? () => handleChallengeJoin(challenge) : undefined}
                    Link={Link}
                    redirectLink={`/${currentLanguage}/communities/${geogroup.id}/challenges/${challenge.id}`}
                    userChallenge={userChallenge}
                  />
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </>
  );
}

export default Challenges;
